import {materialUnitLabel} from "./rsoMaterialForecastHelpers";

const record = (value) => value !== null && typeof value === "object" && !Array.isArray(value);
const number = (value) => typeof value === "number" && Number.isFinite(value) && value >= 0;
const text = (value) => typeof value === "string" && value.trim() ? value.trim() : null;
const list = (value) => Array.isArray(value) ? value : [];

export const validHistorySnapshot = (snapshot) => record(snapshot) && snapshot.schemaVersion === 1 &&
  ["not_entered","saved","legacy"].includes(snapshot.state) &&
  (snapshot.forecastUnits === null || number(snapshot.forecastUnits)) &&
  Array.isArray(snapshot.layouts) && snapshot.layouts.every(record) &&
  Array.isArray(snapshot.materials) && snapshot.materials.every(record);

export const beforeForecastSnapshot = (property) => {
  if (validHistorySnapshot(property.savedSnapshot)) return property.savedSnapshot;
  const saved = property.forecastRevision > 0 || property.submittedOn || property.forecastUnits != null ||
    (property.layouts || []).some((row) => row.forecastUnits != null);
  return {schemaVersion:1,state:saved ? "legacy" : "not_entered",forecastUnits:saved ? property.forecastUnits ?? null : null,
    materialMode:saved ? "not_recorded" : "automatic",isManualException:!!property.isManualException,
    layouts:(property.layouts || []).map((row) => ({unitTypeId:row.unitTypeId,floorplanName:saved ? null : row.floorplanName,
      forecastUnits:saved ? row.forecastUnits ?? null : null})),
    // Current catalog names cannot stand in for missing historical labels.
    materials:saved ? [] : [...new Map((property.layouts || []).flatMap((row) => row.materials || []).map((row) => {
      const name = text(row.privateName) || text([row.manufacturer,row.style,row.color].filter(Boolean).join(" "));
      return [row.productId,{sourceProductId:row.productId,sourceProductName:name,productId:row.productId,
        productName:name,quantity:null,uom:row.uom}];
    })).values()],
  };
};

export const draftForecastSnapshot = (property,counts,materials) => ({
  schemaVersion:1,state:"saved",forecastUnits:counts.complete ? counts.total : null,
  materialMode:materials.custom ? "custom" : "automatic",isManualException:!!property.isManualException,
  layouts:counts.layouts.map((row) => ({unitTypeId:row.unitTypeId,floorplanName:row.floorplanName,forecastUnits:row.forecastUnits})),
  materials:materials.rows.map((row) => ({sourceProductId:row.sourceProductId ?? null,
    sourceProductName:row.sourceProductName || row.productName || null,productId:row.productId || null,
    productName:row.productName || null,quantity:row.parsed?.value ?? row.quantity ?? null,uom:row.uom || null})),
});

const unknown = (snapshot) => snapshot?.state === "not_entered" ? "Not entered" : "Not recorded";
const format = (value,snapshot) => number(value) ? new Intl.NumberFormat(undefined,{maximumFractionDigits:4}).format(value) : unknown(snapshot);
const materialKey = (row) => Number.isInteger(row.sourceProductId) && row.sourceProductId > 0 ? `source:${row.sourceProductId}`
  : Number.isInteger(row.productId) && row.productId > 0 ? `legacy:${row.productId}` : null;

export const forecastComparisonRows = (before,after) => {
  const rows = [];
  const beforeLayouts = new Map(list(before?.layouts).map((row) => [row.unitTypeId,row]));
  const afterLayouts = new Map(list(after?.layouts).map((row) => [row.unitTypeId,row]));
  [...new Set([...beforeLayouts.keys(),...afterLayouts.keys()])].forEach((key) => {
    const old = beforeLayouts.get(key), next = afterLayouts.get(key);
    rows.push({key:`units:${key}`,label:`${text(next?.floorplanName) || text(old?.floorplanName) || "Unit type not recorded"} units`,
      before:format(old?.forecastUnits,before),after:format(next?.forecastUnits,after)});
  });
  rows.push({key:"total",label:"Total units",before:format(before?.forecastUnits,before),after:format(after?.forecastUnits,after)});
  const oldMaterials = new Map(list(before?.materials).map((row,index) => [materialKey(row) || `old-unknown:${index}`,row]));
  const nextMaterials = new Map(list(after?.materials).map((row,index) => [materialKey(row) || `new-unknown:${index}`,row]));
  const hasUnmapped = [...oldMaterials.keys(),...nextMaterials.keys()].some((key) => !key.startsWith("source:")) ||
    (before?.state === "legacy" && oldMaterials.size === 0);
  const allMaterialKeys = [...new Set([...oldMaterials.keys(),...nextMaterials.keys()])];
  // Pair rows only where the same source identity was actually recorded on
  // both sides. Remaining legacy aggregates are whole before/after lists;
  // presenting them as separate arrows would suggest a split we do not know.
  const materialKeys = hasUnmapped ? allMaterialKeys.filter((key) => key.startsWith("source:") && oldMaterials.has(key) && nextMaterials.has(key)) : allMaterialKeys;
  const quantity = (row,snapshot) => number(row?.quantity) && text(row.uom)
    ? `${format(row.quantity,snapshot)} ${materialUnitLabel(row.uom)}` : unknown(snapshot);
  materialKeys.forEach((key) => {
    const old = oldMaterials.get(key), next = nextMaterials.get(key);
    const sourceName = text(next?.sourceProductName) || text(old?.sourceProductName);
    const label = materialKeys.length === 1 && !hasUnmapped ? "Material" : sourceName ? `Material · ${sourceName}` : "Material (original not recorded)";
    rows.push({key:`material:${key}`,label,before:text(old?.productName) || "Not recorded",after:text(next?.productName) || "Not recorded"});
    rows.push({key:`quantity:${key}`,label:materialKeys.length === 1 && !hasUnmapped ? "Amount needed" : `Amount · ${sourceName || "material"}`,
      before:quantity(old,before),after:quantity(next,after)});
  });
  if (hasUnmapped) {
    const remaining = (materials,snapshot) => [...materials.entries()].filter(([key]) => !materialKeys.includes(key))
      .map(([,row]) => `${text(row.productName) || "Not recorded"} · ${quantity(row,snapshot)}`);
    const beforeItems = remaining(oldMaterials,before), afterItems = remaining(nextMaterials,after);
    rows.push({key:"material:unmapped",label:"Materials",note:"Original-to-replacement mapping not recorded.",
      before:beforeItems.join("; ") || "Not recorded",after:afterItems.join("; ") || "Not recorded",beforeItems,afterItems});
  } else if (!materialKeys.length) rows.push({key:"material:unknown",label:"Material",before:"Not recorded",after:"Not recorded"});
  if (before?.materialMode !== after?.materialMode) {
    const mode = (value) => ({automatic:"Current setup",custom:"Selected material"}[value] || "Not recorded");
    rows.push({key:"mode",label:"Material choice",before:mode(before?.materialMode),after:mode(after?.materialMode)});
  }
  return rows;
};

export const readHistoryResponse = (data,contactInfoId,forecastMonth) => {
  if (!record(data) || (data.responsecode !== undefined && String(data.responsecode) !== "200") ||
      (data.responsestatus !== undefined && data.responsestatus !== true && data.responsestatus !== "Success") || data.error) throw new Error("History unavailable");
  const payload = data.result === undefined ? data : Array.isArray(data.result) && data.result.length === 1 ? data.result[0] : data.result;
  if (!record(payload) || payload.contactInfoId !== contactInfoId || !Array.isArray(payload.items) || payload.items.length > 20 ||
      (payload.nextCursor != null && (typeof payload.nextCursor !== "string" || !payload.nextCursor))) throw new Error("History unavailable");
  const ids = new Set();
  if (payload.items.some((event) => {
    if (!record(event) || !Number.isSafeInteger(event.id) || event.id < 1 || ids.has(event.id) || event.contactInfoId !== contactInfoId ||
        !/^\d{4}-(0[1-9]|1[0-2])-01$/.test(event.forecastMonth) || (forecastMonth && event.forecastMonth !== forecastMonth) ||
        !validHistorySnapshot(event.before) || !validHistorySnapshot(event.after)) return true;
    ids.add(event.id); return false;
  })) throw new Error("History unavailable");
  return {items:payload.items,nextCursor:payload.nextCursor || null};
};
